import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const DELIVERY_METHODS = [
  { value: 'transfermovil', label: 'Transfermóvil', icon: '📱', desc: 'App bancaria cubana' },
  { value: 'cash',          label: 'Efectivo',       icon: '💵', desc: 'Entrega en mano' },
  { value: 'cubacel',       label: 'Cubacel',         icon: '📶', desc: 'Recarga móvil' },
];

const PAYMENT_METHODS = [
  { value: 'BTC',             label: 'Bitcoin',         icon: '₿',  desc: '~10 min confirmación', type: 'btcpay' },
  { value: 'BTC_LightningLike', label: 'Lightning',    icon: '⚡', desc: 'Instantáneo',           type: 'btcpay' },
  { value: 'usdt_trc20',      label: 'USDT TRC-20',     icon: '₮',  desc: 'Red TRON, sin fees',   type: 'btcpay' },
  { value: 'zelle',           label: 'Zelle',           icon: '$',  desc: 'Solo EE.UU.',           type: 'manual' },
];

function useQuote(amount, paymentMethod) {
  const [q, setQ] = React.useState(null);
  React.useEffect(() => {
    if (!amount || amount <= 0) { setQ(null); return; }
    const t = setTimeout(() => {
      api.get('/api/v1/quote/', { params: { amount, currency: 'USD', payment_method: paymentMethod } })
        .then(r => setQ(r.data))
        .catch(() => setQ(null));
    }, 400);
    return () => clearTimeout(t);
  }, [amount, paymentMethod]);
  return q;
}

export default function SendRemesa() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=form, 2=payment, 3=confirm, 4=done
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({
    recipient_name: '', recipient_phone: '', recipient_province: 'La Habana',
    amount_usd: '', delivery_method: 'transfermovil',
  });
  const [paymentMethod, setPaymentMethod] = useState('BTC_LightningLike');
  const [order, setOrder] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [receiptImage, setReceiptImage] = useState(null);
  const [txid, setTxid] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    api.get('/api/v1/contacts/').then(r => setContacts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    return () => { if (pollingInterval) clearInterval(pollingInterval); };
  }, []);

  const q = useQuote(parseFloat(form.amount_usd) || 0, paymentMethod);
  const fee = q ? Number(q.sender_fee).toFixed(2) : '…';
  const total = q ? Number(q.sender_pays).toFixed(2) : '…';
  const cup = q ? Number(q.cup_to_deliver).toLocaleString('es') : '…';

  // Step 1 → Create order
  const handleCreateOrder = async () => {
    if (!form.recipient_name || !form.recipient_phone || !form.amount_usd) {
      setError('Completa todos los campos'); return;
    }
    if (parseFloat(form.amount_usd) < 5) {
      setError('Monto mínimo $5'); return;
    }
    setLoading(true); setError('');
    try {
      const res = await api.post('/api/v1/transactions/', {
        ...form,
        amount_usd: parseFloat(form.amount_usd),
        currency_in: 'USD',
        status: 'pending_payment',
      });
      setOrder(res.data);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al crear la orden');
    } finally { setLoading(false); }
  };

  // Step 2 → Initiate payment
  const handleInitiatePayment = async () => {
    if (!order) return;
    setLoading(true); setError('');
    try {
      const res = await api.post(`/api/v1/btcpay/pay/${order.ref}`, {
        payment_method: paymentMethod,
      });
      setPaymentInfo(res.data);
      setStep(3);

      // For BTCPay — open checkout in new tab + start polling
      if (res.data.type === 'btcpay') {
        window.open(res.data.checkout_url, '_blank');
        startPolling(order.ref);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al iniciar el pago');
    } finally { setLoading(false); }
  };

  // Poll payment status for BTC/Lightning
  const startPolling = (ref) => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/api/v1/btcpay/payment-status/${ref}`);
        if (res.data.paid) {
          clearInterval(interval);
          setStep(4);
        }
      } catch { }
    }, 5000);
    setPollingInterval(interval);
  };

  // Step 3 → Upload receipt for manual methods
  const handleUploadReceipt = async () => {
    if (!receiptImage && !txid) {
      setError('Sube el comprobante o ingresa el TXID'); return;
    }
    setLoading(true); setError('');
    try {
      await api.post(`/api/v1/btcpay/manual/${order.ref}/receipt`, {
        receipt_image: receiptImage,
        txid: txid || null,
        note: `Pago via ${paymentMethod}`,
      });
      setStep(4);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al subir el comprobante');
    } finally { setLoading(false); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setReceiptImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const selectedPayment = PAYMENT_METHODS.find(m => m.value === paymentMethod);
  const isManual = selectedPayment?.type === 'manual';

  // ── STEP 1: Form ──────────────────────────────────────────────────
  if (step === 1) return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>Nuevo servicio</h1>
      <p style={{ fontSize: 13, color: '#7a7060', marginBottom: 24 }}>Coordinación de envío — tasa elToque en vivo, fee según el monto</p>

      {error && <div style={{ background: '#faeef4', border: '1px solid #f4b8c8', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#9b2c5e' }}>⚠ {error}</div>}

      <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 24, marginBottom: 16 }}>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 15, marginBottom: 16 }}>Destinatario en Cuba</div>

        {/* Saved contacts */}
        {contacts.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 8 }}>Contactos guardados</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {contacts.map((c, i) => (
                <button key={i}
                  onClick={() => setForm(f => ({ ...f, recipient_name: c.full_name, recipient_phone: c.phone_cuba, recipient_province: c.province || f.recipient_province }))}
                  style={{ padding: '6px 12px', borderRadius: 20, border: `1px solid ${form.recipient_phone === c.phone_cuba ? '#c94a1e' : '#e2dbd0'}`, background: form.recipient_phone === c.phone_cuba ? '#fdf4f0' : '#fff', fontSize: 12, cursor: 'pointer', color: form.recipient_phone === c.phone_cuba ? '#c94a1e' : '#4a4035' }}>
                  {c.full_name}
                </button>
              ))}
            </div>
          </div>
        )}

        {[
          { key: 'recipient_name', label: 'NOMBRE', placeholder: 'María García' },
          { key: 'recipient_phone', label: 'TELÉFONO CUBA', placeholder: '+53 5234 5678' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>{f.label}</label>
            <input style={{ width: '100%', padding: '11px 14px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              placeholder={f.placeholder}
              value={form[f.key]}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
          </div>
        ))}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>PROVINCIA DE ENTREGA</label>
          <select
            style={{ width: '100%', padding: '11px 14px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fff' }}
            value={form.recipient_province}
            onChange={e => setForm(p => ({ ...p, recipient_province: e.target.value }))}>
            {['La Habana','Artemisa','Mayabeque','Pinar del Río','Matanzas','Cienfuegos','Villa Clara','Sancti Spíritus','Ciego de Ávila','Camagüey','Las Tunas','Holguín','Granma','Santiago de Cuba','Guantánamo','Isla de la Juventud'].map(p => <option key={p}>{p}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>MONTO A ENVIAR (USD)</label>
          <input
            style={{ width: '100%', padding: '11px 14px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 28, fontWeight: 700, outline: 'none', boxSizing: 'border-box' }}
            type="number" placeholder="100" min="5"
            value={form.amount_usd}
            onChange={e => setForm(p => ({ ...p, amount_usd: e.target.value }))} />
          {form.amount_usd && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#7a7060' }}>
              <span>Fee{q ? ` (${q.sender_fee_pct})` : ''}: <b>${fee}</b></span>
              <span>Total a pagar: <b style={{ color: '#c94a1e' }}>${total}</b></span>
            </div>
          )}
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 8 }}>MÉTODO DE ENTREGA</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {DELIVERY_METHODS.map(m => (
              <div key={m.value} onClick={() => setForm(f => ({ ...f, delivery_method: m.value }))}
                style={{ padding: '12px 8px', border: `2px solid ${form.delivery_method === m.value ? '#c94a1e' : '#e2dbd0'}`, borderRadius: 10, cursor: 'pointer', textAlign: 'center', background: form.delivery_method === m.value ? '#fdf4f0' : '#fff' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>{m.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700 }}>{m.label}</div>
                <div style={{ fontSize: 10, color: '#7a7060' }}>{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button onClick={handleCreateOrder} disabled={loading} style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: loading ? '#e2dbd0' : '#c94a1e', color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
        {loading ? 'Creando orden...' : 'Continuar al pago →'}
      </button>
    </div>
  );

  // ── STEP 2: Choose payment method ─────────────────────────────────
  if (step === 2) return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7a7060', fontSize: 13, marginBottom: 20 }}>← Volver</button>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 22, margin: '0 0 4px' }}>Método de pago</h2>
      <p style={{ fontSize: 13, color: '#7a7060', marginBottom: 20 }}>
        Orden <b style={{ fontFamily: 'monospace', color: '#c94a1e' }}>{order?.ref}</b> · Total: <b>${total}</b>
      </p>

      {error && <div style={{ background: '#faeef4', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#9b2c5e' }}>⚠ {error}</div>}

      <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, overflow: 'hidden', marginBottom: 16 }}>
        {PAYMENT_METHODS.map((m, i) => (
          <div key={m.value} onClick={() => setPaymentMethod(m.value)}
            style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: i < PAYMENT_METHODS.length - 1 ? '1px solid #f0ebe2' : 'none', cursor: 'pointer', background: paymentMethod === m.value ? '#fdf4f0' : '#fff' }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: paymentMethod === m.value ? '#c94a1e15' : '#f0ebe2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
              {m.icon}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{m.label}</div>
              <div style={{ fontSize: 12, color: '#7a7060' }}>{m.desc}</div>
            </div>
            <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${paymentMethod === m.value ? '#c94a1e' : '#e2dbd0'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {paymentMethod === m.value && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#c94a1e' }} />}
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div style={{ background: '#f9f6f1', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
        {[
          ['Para', `${form.recipient_name} · ${form.recipient_phone}`],
          ['Entrega', DELIVERY_METHODS.find(m => m.value === form.delivery_method)?.label],
          ['Recibe en Cuba', q ? `${cup} CUP` : '…'],
          ['Tasa', q ? `1 USD = ${q.rate} CUP` : '…'],
          ['Fee coordinación', `$${fee}`],
          ['Total a pagar', `$${total}`],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e2dbd0', fontSize: 13 }}>
            <span style={{ color: '#7a7060' }}>{k}</span>
            <span style={{ fontWeight: k === 'Total a pagar' ? 700 : 600, color: k === 'Total a pagar' ? '#c94a1e' : '#0f0d0b' }}>{v}</span>
          </div>
        ))}
      </div>

      <button onClick={handleInitiatePayment} disabled={loading} style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: loading ? '#e2dbd0' : '#c94a1e', color: '#fff', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
        {loading ? 'Procesando...' : `Pagar $${total} con ${selectedPayment?.label} →`}
      </button>
    </div>
  );

  // ── STEP 3: Payment instructions / receipt upload ─────────────────
  if (step === 3) return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 22, margin: '0 0 4px' }}>
        {isManual ? 'Instrucciones de pago' : 'Esperando pago...'}
      </h2>
      <p style={{ fontSize: 13, color: '#7a7060', marginBottom: 20 }}>
        Orden <b style={{ fontFamily: 'monospace', color: '#c94a1e' }}>{order?.ref}</b>
      </p>

      {error && <div style={{ background: '#faeef4', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#9b2c5e' }}>⚠ {error}</div>}

      {/* BTCPay — waiting */}
      {!isManual && paymentInfo && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 24, textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{selectedPayment?.icon}</div>
          <h3 style={{ fontFamily: 'Georgia,serif', fontSize: 18, marginBottom: 8 }}>Ventana de pago abierta</h3>
          <p style={{ fontSize: 13, color: '#7a7060', marginBottom: 16 }}>
            Se abrió una nueva ventana con el checkout de BTCPay. Completa el pago allí.
          </p>
          <div style={{ background: '#eaf1fb', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1a4e8c', marginBottom: 16 }}>
            Esta página verificará el pago automáticamente cada 5 segundos.
          </div>
          <button onClick={() => window.open(paymentInfo.checkout_url, '_blank')} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #e2dbd0', background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
            Abrir ventana de pago de nuevo
          </button>
        </div>
      )}

      {/* Manual — instructions */}
      {isManual && paymentInfo && (
        <div>
          <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 22, marginBottom: 16 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, marginBottom: 14 }}>{paymentInfo.title}</div>

            {/* Steps */}
            {paymentInfo.steps?.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#c94a1e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                <span style={{ fontSize: 13, color: '#4a4035', lineHeight: 1.5 }}>{step}</span>
              </div>
            ))}

            {/* Address to pay */}
            <div style={{ background: '#f9f6f1', borderRadius: 8, padding: '12px 14px', marginTop: 14 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>
                {paymentMethod === 'usdt_trc20' ? 'Dirección TRC-20' : 'Enviar a'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#c94a1e', wordBreak: 'break-all' }}>{paymentInfo.address}</span>
                <button onClick={() => { navigator.clipboard.writeText(paymentInfo.address); }} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e2dbd0', background: '#fff', fontSize: 11, cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>
                  Copiar
                </button>
              </div>
            </div>

            {paymentInfo.memo && (
              <div style={{ background: '#fef3e2', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 12, color: '#b45309' }}>
                ⚠ <b>Memo obligatorio:</b> {paymentInfo.memo}
              </div>
            )}

            <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, fontWeight: 700, color: '#c94a1e', textAlign: 'center', margin: '16px 0 4px' }}>
              ${total} USD
            </div>
          </div>

          {/* Upload receipt */}
          <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 22 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, marginBottom: 14 }}>Subir comprobante</div>

            {paymentMethod === 'usdt_trc20' ? (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>TXID (hash de la transacción)</label>
                <input style={{ width: '100%', padding: '11px 14px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace' }}
                  placeholder="0x1234abcd..."
                  value={txid}
                  onChange={e => setTxid(e.target.value)} />
              </div>
            ) : null}

            {!receiptImage ? (
              <label style={{ display: 'block', padding: '24px', border: '2px dashed #e2dbd0', borderRadius: 10, textAlign: 'center', cursor: 'pointer', marginBottom: 14 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📎</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#7a7060' }}>Subir captura de pantalla del pago</div>
                <div style={{ fontSize: 11, color: '#b0a090', marginTop: 4 }}>JPG, PNG — máx 5MB</div>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
              </label>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <img src={receiptImage} alt="Comprobante" style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2dbd0', marginBottom: 8 }} />
                <button onClick={() => setReceiptImage(null)} style={{ fontSize: 12, color: '#7a7060', background: 'none', border: 'none', cursor: 'pointer' }}>↩ Cambiar imagen</button>
              </div>
            )}

            <button onClick={handleUploadReceipt} disabled={loading || (!receiptImage && !txid)} style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: (loading || (!receiptImage && !txid)) ? '#e2dbd0' : '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: (loading || (!receiptImage && !txid)) ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Enviando...' : 'Enviar comprobante →'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ── STEP 4: Done ──────────────────────────────────────────────────
  if (step === 4) return (
    <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', paddingTop: 32 }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>
        {isManual ? '⏳' : '✅'}
      </div>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 12px' }}>
        {isManual ? '¡Comprobante recibido!' : '¡Pago confirmado!'}
      </h2>
      <p style={{ fontSize: 14, color: '#7a7060', lineHeight: 1.7, marginBottom: 24 }}>
        {isManual
          ? 'Verificaremos tu pago en menos de 2 horas hábiles. Recibirás una notificación cuando tu orden esté activa.'
          : 'Tu orden está activa. Un agente coordinará la entrega.'}
      </p>

      <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 20, marginBottom: 24 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 18, color: '#c94a1e', fontWeight: 700, marginBottom: 8 }}>{order?.ref}</div>
        <div style={{ fontSize: 13, color: '#7a7060' }}>{form.recipient_name} · ${net}</div>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => navigate('/app/historial')} style={{ flex: 1, padding: '13px', borderRadius: 10, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          Ver mis órdenes →
        </button>
        <button onClick={() => { setStep(1); setForm({ recipient_name: '', recipient_phone: '', recipient_province: 'La Habana', amount_usd: '', delivery_method: 'transfermovil' }); setOrder(null); setPaymentInfo(null); setReceiptImage(null); setTxid(''); }} style={{ flex: 1, padding: '13px', borderRadius: 10, border: '1px solid #e2dbd0', background: '#fff', fontSize: 14, cursor: 'pointer' }}>
          Nueva orden
        </button>
      </div>
    </div>
  );

  return null;
}
